/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 * 서비스에서 다루고 있는 timezone을 기준으로 날짜를 생성하는 함수
 * 로컬 사용자의 timezone은 new Date로 다루세요.
 *
 * @created at Fri Sep 20 2024
 **/

import dayjs, { Dayjs } from "@/modules/init_dayjs";

import Language, { _T } from "@rfice/language";
import RDate from "./date";

class ChronoUtilsStatic {
	/**
	 * Returns the timezone function from the `dayjs` library.
	 *
	 * @return {typeof dayjs.tz} The `timezone` function from `dayjs`.
	 */
	public get timezone(): typeof dayjs.tz {
		return dayjs.tz;
	}

	/**
	 * Guesses and returns the local timezone based on system settings.
	 *
	 * @return {string} The guessed timezone identifier.
	 */
	public get guess_timezone(): string {
		return this.timezone.guess();
	}

	public time_format(key: string) {
		return (
			{
				"MM.DD": ["ja", "ko"].includes(Language.current()) ? "MM.DD" : "DD.MM",
				"YYYY.MM.DD": ["ja", "ko"].includes(Language.current()) ? "YYYY.MM.DD" : "DD.MM.YYYY",
				"YY.M.D": ["ja", "ko"].includes(Language.current()) ? "YY.M.D" : "M.D.YY",
				["M월 D일"]: Language.current() === "ko" ? "M월 D일" : Language.current() === "ja" ? "M月D日" : "D. M",
				["DateInThisYear"]:
					Language.current() === "ko" ? "M월 D일" : Language.current() === "ja" ? "M月D日" : "MMM DD",
				["DateOutThisYear"]:
					Language.current() === "ko"
						? "YYYY년 MM월 DD일"
						: Language.current() === "ja"
							? "YYYY年 MM月 DD日"
							: "MMM DD YYYY",
				"YYYY.MM.DD HH:mm:ss": ["ja", "ko"].includes(Language.current())
					? "YYYY.MM.DD HH:mm:ss"
					: "MM.DD.YYYY HH:mm:ss",
				"A hh:mm": ["ja", "ko"].includes(Language.current()) ? "A hh:mm" : "hh:mm A",
				"h:mm A": ["ja", "ko"].includes(Language.current()) ? "h:mm A" : "h:mm A",
				"h:mm": ["ja", "ko"].includes(Language.current()) ? "h:mm" : "h:mm",
				Within7DaysToThisYear: ["ja", "ko"].includes(Language.current()) ? "MM.DD" : "MMM.DD",
				BeforeThisYear: ["ja", "ko"].includes(Language.current()) ? "YYYY.MM.DD" : "MMM.DD.YYYY",
				["From2DaysAgoToCurrentYear"]:
					Language.current() === "ko"
						? "MM월 DD일 dddd"
						: Language.current() === "ja"
							? "MM月 DD日 dddd"
							: "dddd MMM DD",
				["BeforeThisYearDateOnly"]:
					Language.current() === "ko"
						? "YYYY년 MM월 DD일 dddd"
						: Language.current() === "ja"
							? "YYYY年 MM月 DD日 dddd"
							: "dddd MMM DD YYYY"
			}[key] || key
		);
	}

	/**
	 * Gets the current date and time, converted to the specified timezone.
	 *
	 * @return {Date} The current date and time, converted to the specified timezone.
	 */
	public get now(): number {
		return this.convert_date_with_timezone(Date.now());
	}

	/**
	 * Converts a date to a timestamp considering the time zone.
	 *
	 * @param {Date | number} date - The input date which can be a Date object or a timestamp in milliseconds.
	 * @return {number} - The converted timestamp in milliseconds, adjusted for the time zone.
	 */
	public convert_date_with_timezone(date: Date | number): number {
		let _date = date;
		if (typeof date === "number") {
			_date = new Date(date);
		}
		const _date_tz = this.timezone(_date);
		return _date_tz.unix() * 1000 + _date_tz.millisecond();
	}

	/**
	 * Formats a date according to the specified format string. The date can be
	 * provided either as a Date object or as a timestamp number. The method
	 * ensures the date is processed in the correct timezone.
	 *
	 * @param {Date | number} date - The date to format, given as a Date object or a timestamp.
	 * @param {string} format - The string specifying the desired format of the output date.
	 * @return {string} The formatted date string.
	 */
	public format(date: Date | number | Dayjs, format: string): string {
		let _date = date;
		if (typeof date === "number") {
			_date = new Date(date);
		}
		return this.timezone(_date).format(format);
	}

	/**
	 * Returns the name of the weekend day from a given date string.
	 *
	 * @param {string} date - The date string to convert.
	 * @return {string} - The name of the weekend day.
	 */
	public get_day_name(date: string): string {
		const _date = this.timezone(date);
		return _date.format("dddd");
	}

	/**
	 * Sets the default timezone for the application.
	 *
	 * If no timezone is provided, the system will attempt to guess the timezone based on the environment.
	 * The guessed or provided timezone will then be set as the default for the application.
	 *
	 * @param {string} [timezone] - The desired timezone to set as default. Optional.
	 * @return {void}
	 */
	public set_timezone(timezone?: string): void {
		if (!timezone) {
			timezone = dayjs.tz.guess();
		}
		// timezone이 없을 경우 기본값을 설정
		RDate.setDefault(timezone);
		this.timezone.setDefault(timezone);
	}

	/**
	 * Computes the difference in days (or specified unit) between two timestamps.
	 *
	 * @param {number} timestamp1 - The first timestamp to be compared.
	 * @param {number} timestamp2 - The second timestamp to be compared.
	 * @param {dayjs.OpUnitType} [unit="day"] - The unit of measurement for the difference.
	 * @return {number} The difference between the two timestamps in the specified unit.
	 */
	public getDayDiff(timestamp1: number, timestamp2: number, unit: dayjs.OpUnitType = "day"): number {
		const _timestamp1 = this.timezone(timestamp1);
		const _timestamp2 = this.timezone(timestamp2);
		return this.timezone(_timestamp1.startOf(unit)).diff(_timestamp2.startOf(unit), unit);
	}

	/**
	 * Formats the given timestamp to a 12-hour clock time string.
	 *
	 * @param {number} timestamp - The timestamp to be formatted.
	 * @return {string} - The formatted 12-hour time string.
	 */
	public formatTimestamp12Hours(timestamp: number): string {
		const date = this.timezone(timestamp);

		let hours = date.get("hours");
		const minutes = date.get("minutes");

		const amOrPm = hours >= 12 ? _T("오후") : _T("오전");

		hours = hours % 12;
		hours = hours ? hours : 12;

		return `${amOrPm} ${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
	}

	/**
	 * Converts a given timestamp to a formatted time string following the country's language and time format conventions.
	 *
	 * @param {number} timestamp - The timestamp to be converted.
	 * @return {string} - The formatted time string according to the country's conventions.
	 */
	public timestampFollowCountry(timestamp: number): string {
		const date = this.timezone(timestamp);
		const isLanguageKoAndJa = ["ja", "ko"].includes(Language.current());

		let hours = date.get("hours");
		const amOrPm = hours >= 12 ? _T("오후") : _T("오전");

		const minutes = date.get("minutes");
		hours = hours % 12;
		hours = hours ? hours : 12;

		const time = `${hours}:${minutes.toString().padStart(2, "0")}`;
		return isLanguageKoAndJa ? `${amOrPm} ${time}` : `${time} ${amOrPm}`;
	}

	/**
	 * Formats a given timestamp into a human-readable "time ago" string.
	 *
	 * @param {number} timestamp - The timestamp to be formatted.
	 * @return {string} A string representing the formatted "time ago" result.
	 */
	public formatTimeAgo(timestamp: number): string {
		const now = this.timezone();

		const targetDate = this.timezone(timestamp);

		if (now.isSame(targetDate, "day")) {
			return this.formatTimestamp12Hours(timestamp);
		} else if (isYesterday(targetDate)) {
			return _T("어제");
		} else if (now.date() - targetDate.date() >= 2 && now.year() === targetDate.year()) {
			return targetDate.format(this.time_format("MM.DD"));
		} else {
			return targetDate.format(this.time_format("YYYY.MM.DD"));
		}

		function isYesterday(targetDate: Dayjs) {
			return now.subtract(1, "day").isSame(targetDate, "day");
		}
	}

	/**
	 * Formats a given timestamp into a date string representing how long ago it was from the current time.
	 *
	 * @param {number} timestamp - The timestamp to be formatted.
	 * @return {string} A string representation of the date in 'YYYY.MM.DD' format.
	 */
	public formatDateAgoMessage(timestamp: number): string {
		const targetDate = this.timezone(timestamp);
		return targetDate.format(this.time_format("YYYY.MM.DD"));
	}

	/**
	 * Formats a given timestamp into a localized date string.
	 *
	 * @param {number} timestamp - The date and time in milliseconds since Unix epoch.
	 * @param {boolean} [isHideWeekDay=false] - Flag to determine whether to hide the weekday in the formatted date.
	 * @return {string} The formatted date string.
	 */
	public formatDate(timestamp: number, isHideWeekDay = false): string {
		const date = new Date(timestamp);
		const currentYear = new Date().getFullYear();
		const year = date.getFullYear();
		const month = date.getMonth() + 1;
		const day = date.getDate();

		if (year === currentYear) {
			return `${
				Language.current() === "ko"
					? `${month}월 ${day}일 ${isHideWeekDay ? "" : date.toLocaleDateString("ko-KR", { weekday: "long" })}`
					: `${isHideWeekDay ? "" : date.toLocaleDateString("en-EN", { weekday: "long" })}${isHideWeekDay ? "" : " "}${date.toLocaleDateString("en-EN", { month: "long" })} ${day}`
			}`;
		} else {
			return `${
				Language.current() === "ko"
					? `${year}년 ${month}월 ${day}일 ${isHideWeekDay ? "" : date.toLocaleDateString("ko-KR", { weekday: "long" })}`
					: `${isHideWeekDay ? "" : date.toLocaleDateString("en-EN", { weekday: "long" })}${isHideWeekDay ? "" : " "}${date.toLocaleDateString("en-EN", { month: "long" })} ${day} ${year}`
			}`;
		}
	}

	/**
	 * Calculates the duration of a meeting based on its start time, returning the result in "HH:MM:SS" format.
	 * If no start time is provided, the function will return an empty string.
	 *
	 * @param {number} [startTimeMillis] - The start time of the meeting in milliseconds (optional).
	 * @returns {string} A formatted string representing the duration in "HH:MM:SS" format, or an empty string if no start time is provided.
	 */
	public calculateMeetingDuration = (startTimeMillis?: number): string => {
		if (!startTimeMillis) return "";
		const currentTimeMillis = this.now;
		const diffInMilliseconds = currentTimeMillis - startTimeMillis;

		const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
		const hours = Math.floor(diffInSeconds / 3600);
		const minutes = Math.floor((diffInSeconds % 3600) / 60);

		const seconds = diffInSeconds % 60;

		const formatNumber = (number: number): string => number.toString().padStart(2, "0");

		return `${formatNumber(hours)}:${formatNumber(minutes)}:${formatNumber(seconds)}`;
	};

	/**
	 * Calculates the duration of a meeting given its start time in milliseconds.
	 *
	 * @param {number} [startTimeMillis] - The start time of the meeting in milliseconds.
	 * @returns {string} - A formatted string representing the duration of the meeting.
	 *                     The format varies based on the current language setting:
	 *                     - For Korean (ko): "X시간 Y분" (hours and minutes in Korean)
	 *                     - For Japanese (ja): "X時間 Y分" (hours and minutes in Japanese)
	 *                     - For other languages: "X hours Y minutes" (hours and minutes in English with singular/plural adjustment).
	 */
	public meetingDuration = (startTimeMillis?: number): string => {
		if (!startTimeMillis) return "";
		const currentTimeMillis = this.now;
		const diffInMilliseconds = currentTimeMillis - startTimeMillis;

		const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
		const hours = Math.floor(diffInSeconds / 3600);
		const minutes = Math.floor((diffInSeconds % 3600) / 60);

		if (Language.current() === "ko") {
			return `${hours}시간 ${minutes}분`;
		}

		if (Language.current() === "ja") {
			return `${hours}時間 ${minutes}分`;
		}

		const formatUnit = (value: number, singular: string, plural: string): string => {
			return `${value} ${value <= 1 ? singular : plural}`;
		};

		const hoursDisplay = formatUnit(hours, "hour", "hours");
		const minutesDisplay = formatUnit(minutes, "minute", "minutes");

		return `${hoursDisplay} ${minutesDisplay}`;
	};

	/**
	 * Converts a given Unix timestamp into a formatted date string.
	 *
	 * @param {number} timestamp - The Unix timestamp to format.
	 * @returns {string} A string representation of the timestamp in the format "YYYY.MM.DD HH:mm:ss".
	 */
	public formatTimestamp = (timestamp: number): string => {
		return dayjs(timestamp).format(this.time_format("YYYY.MM.DD HH:mm:ss"));
	};

	/**
	 * Check if the input date is before the current date with local timezone based on system settings.
	 *
	 * @param {number | string} date - The Unix timestamp or date string.
	 * @param {dayjs.OpUnitType} unit - part of date - default "day".
	 * @returns {boolean} return true if input date is before present".
	 */
	public isBefore = (date: number | string, unit?: dayjs.OpUnitType | dayjs.UnitTypeLongPlural): boolean => {
		return this.timezone(date).isBefore(this.now, unit || "day");
	};

	/**
	 * Check if the input date is the current date with local timezone based on system settings.
	 *
	 * @param {number | string} date - The Unix timestamp or date string.
	 * @returns {boolean} return true if input date is today".
	 */
	public isToday = (date: number | string): boolean => {
		const inputDate = this.timezone(date);
		const now = this.timezone(this.now);
		return inputDate.isSame(now, "day");
	};
	/**
	 * Check if the input date is in this year based on local timezone settings.
	 *
	 * @param {number | string} date - The Unix timestamp or date string.
	 * @returns {boolean} Returns true if the input date is in the current year.
	 */
	public isThisYear = (date: number | string): boolean => {
		const inputDate = this.timezone(date);
		const now = this.timezone(this.now);
		return inputDate.year() === now.year();
	};

	/**
	 * Formats the given unix time number to a localized string including the month, day, and day of the week
	 *
	 * @param {number } unixTime - The Unix timestamp .
	 * @returns {string} A string representation of the timestamp in the format "M D dddd".
	 */

	public convertUnixToDate = (unixTime?: number): string => {
		if (!unixTime) return "";

		const dateWithTz = this.timezone(unixTime);
		const today = this.timezone();

		const formattedDate = dateWithTz.format(this.time_format("M월 D일"));
		const dayName = dateWithTz.format("dddd");

		return dateWithTz.isSame(today, "day")
			? _T("오늘, {{formattedDate}} {{dayName}}", { formattedDate, dayName })
			: `${formattedDate} ${dayName}`;
	};

	/**
	 * Formats the given  unix time number to a localized string hours .
	 *
	 * @param {number } unixTime - The Unix timestamp .
	 * @returns {string} A string representation of the timestamp in the format "A hh:mm".
	 */
	public convertUnixTime = (unixTime?: number): string => {
		if (!unixTime) return "";

		return this.timezone(unixTime).format(this.time_format("A hh:mm"));
	};

	/**
	 * Formats the given timestamp to a 12-hour time string with meridiem (AM/PM).
	 *
	 * @param {number} timestamp - The timestamp in milliseconds.
	 * @returns {string} A string representation of the time in the format "h:mm A".
	 */
	public formatTo12HourWithMeridiem = (timestamp: number): string => {
		return this.timezone(timestamp).format(this.time_format("h:mm A"));
	};

	/**
	 * Formats the given timestamp to a 12-hour time string without meridiem.
	 *
	 * @param {number} timestamp - The timestamp in milliseconds.
	 * @returns {string} A string representation of the time in the format "h:mm".
	 */
	public formatTo12HourWithoutMeridiem = (timestamp: number): string => {
		return this.timezone(timestamp).format(this.time_format("h:mm"));
	};

	/**
	 * Formats a given timestamp into a human-readable "time ago" string.
	 *
	 * @param {number} timestamp - The timestamp in milliseconds.
	 * @returns {string} A string representation of the time in the format "BeforeThisYearDateOnly"".
	 */
	public formatBeforeThisYearDateOnly = (timestamp: number): string => {
		const targetDate = this.timezone(timestamp);

		return targetDate.format(this.time_format("BeforeThisYearDateOnly"));
	};

	/**
	 * Formats a given timestamp into a human-readable date string, including the day of the week.
	 * If the timestamp is from the current year, it returns the date with the day of the week in a format specific to the current year.
	 * If the timestamp is from a previous year, it returns the full date (with the day of the week) and includes the year.
	 *
	 * @param {number} timestamp - The timestamp in milliseconds.
	 * @returns {string} A formatted string representing the date, with the day of the week, either in the format for the current year or with the full date for previous years.
	 */
	public formatFullDateWithDayOfWeek = (timestamp: number): string => {
		const targetDate = this.timezone(timestamp);

		return this.isThisYear(targetDate.valueOf())
			? targetDate.format(this.time_format("From2DaysAgoToCurrentYear"))
			: targetDate.format(this.time_format("BeforeThisYearDateOnly"));
	};

	/**
	 * Formats a given timestamp into a human-readable date string.
	 * If the timestamp is from the current year, it returns the date in a format suitable for the current year.
	 * If the timestamp is from a previous year, it returns the full date with the year included.
	 *
	 * @param {number} timestamp - The timestamp in milliseconds.
	 * @returns {string} A formatted string representing the date, either with the year (for dates outside the current year) or without it (for dates in the current year).
	 */
	public formatDateByYear = (timestamp: number): string => {
		const targetDate = this.timezone(timestamp);

		return this.isThisYear(targetDate.valueOf())
			? targetDate.format(this.time_format("DateInThisYear"))
			: targetDate.format(this.time_format("DateOutThisYear"));
	};

	/**
	 * Formats a given timestamp into a human-readable "time ago" or "date only" string.
	 *
	 * @param {number} timestamp - The timestamp in milliseconds to be formatted.
	 * @return {string} - A localized string representing the date, formatted according to the following rules:
	 *                    - Same day: Returns "오늘" (Today in Korean).
	 *                    - Yesterday: Returns "어제" (Yesterday in Korean).
	 *                    - Within the current year: Formats the date using the pattern defined in `From2DaysAgoToCurrentYear`.
	 *                    - Before the current year: Formats the date using the pattern defined in `BeforeThisYearDateOnly`.
	 */
	public formatPolicyDateOnly = (timestamp: number): string => {
		const targetDate = this.timezone(timestamp);
		const today = this.timezone(this.now);

		if (targetDate.isToday()) {
			return `${_T("오늘")}`;
		} else if (targetDate.isSame(today.subtract(1, "day"), "day")) {
			return `${_T("어제")}`;
		} else if (targetDate.year() === today.year()) {
			return `${targetDate.format(this.time_format("From2DaysAgoToCurrentYear"))}`;
		} else {
			return `${targetDate.format(this.time_format("BeforeThisYearDateOnly"))}`;
		}
	};

	/**
	 * Formats a given timestamp into a localized "time ago" string based on the current country's time display rules.
	 *
	 * @param {number} timestamp - The timestamp to be formatted.
	 * @return {string} - A localized string representing the time relative to the current time, with the following logic:
	 *                    - Same day: Displays the time using `timestampFollowCountry`.
	 *                    - Yesterday: Displays "어제" (Yesterday in Korean) and the time using `timestampFollowCountry`.
	 *                    - 2-6 days ago: Displays the day of the week (e.g., Mon, Tue) and the time using `timestampFollowCountry`.
	 *                    - Within the current year: Displays a date formatted with `Within7DaysToThisYear` and the time.
	 *                    - Before the current year: Displays a date formatted with `BeforeThisYear` and the time.
	 */
	public formatTimeChatTime(timestamp: number): string {
		const date = this.timezone(timestamp);
		const today = this.timezone(this.now);
		const currentYear = today.year();
		const year = date.year();

		const diffInDays = today.diff(date, "day");

		const now = this.timezone();

		if (now.isSame(date, "day")) {
			return this.timestampFollowCountry(timestamp);
		} else if (date.isSame(today.subtract(1, "day"), "day")) {
			return `${_T("어제")}, ${this.timestampFollowCountry(timestamp)}`;
		} else if (diffInDays >= 2 && diffInDays <= 6) {
			return `${date.format("ddd")}, ${this.timestampFollowCountry(timestamp)}`;
		} else if (year === currentYear) {
			return `${date.format(this.time_format("Within7DaysToThisYear"))}, ${this.timestampFollowCountry(timestamp)}`;
		} else {
			return `${date.format(this.time_format("BeforeThisYear"))}, ${this.timestampFollowCountry(timestamp)}`;
		}
	}

	/**
	 * Converts a given timestamp (in seconds) into a formatted "MM:SS" string.
	 * The function calculates the number of minutes and seconds from the timestamp,
	 * and ensures that both minutes and seconds are always displayed with two digits.
	 *
	 * @param {number} time - The timestamp in seconds to be formatted.
	 * @return {string} - A string representing the time in "MM:SS" format.
	 */
	public calculateRecording(time: number) {
		return `${String(Math.floor(time / 60)).padStart(2, "0")}:${String(time % 60).padStart(2, "0")}`;
	}

	/**
	 * Calculates and formats the duration of a voice chat based on start and end timestamps.
	 * The function computes the time difference between endTime and startTime (in milliseconds),
	 * converts it to hours, minutes, and seconds, and returns a formatted string.
	 * If hours are present, the format is "HH:MM:SS"; otherwise, it is "MM:SS".
	 * The result is wrapped in the pattern "%보이스챗 진행시간 [time]%".
	 *
	 * @param {number} startTime - The voice chat startTime in milliseconds.
	 * @param {number} endTime - The voice chat endTime in milliseconds.
	 * @return {string} - A string representing the duration in "%보이스챗 진행시간 HH:MM:SS%" or "%보이스챗 진행시간 MM:SS%" format.
	 */
	public formatVoiceChatDuration(startTime: number, endTime: number) {
		const durationMs = endTime - startTime;
		const totalSeconds = Math.floor(durationMs / 1000);
		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;

		let timeString: string;
		if (hours > 0) {
			timeString = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
		} else {
			timeString = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
		}

		return _T("보이스챗이 종료되었습니다.({{time}})", { time: timeString });
	}
}

const ChronoUtils = Object.freeze(new ChronoUtilsStatic());
export default ChronoUtils;
